import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getAirport, getAirports } from './airportService';

/**
 * @description Hook to get airports
 * @param {{search: string, pageSize: number, pageNumber: number }} searchParams - The search parameters.
 * @param {import('@tanstack/react-query').QueriesOptions<any>} [options={}] - The query options
 * @returns {import('@tanstack/react-query').UseQueryResult<
 * {data: Airport[], total: number, pageNumber: number}>}
 */
export const useGetAirports = (searchParams, options = {}) => useQuery({
  queryKey: ['get-airports', searchParams],
  queryFn: () => getAirports(searchParams),
  ...options,
});

/**
 * @description Get the list of airports with infinite pagination.
 * @param {object} [params] The params
 * @param {{search?: string, pageSize?: number}} [params.queryParams] The search params.
 * @param {object} [params.options] The query options.
 * @returns {import('@tanstack/react-query').UseInfiniteQueryResult<any, Error>} The query result.
 */
export const useGetAirportsInfiniteQuery = (params = {}) => {
  const { queryParams = {}, options = {} } = params;
  return useInfiniteQuery({
    queryKey: ['get-airports-infinite', queryParams],
    queryFn: ({ pageParam = 1 }) => getAirports({
      search: queryParams.search || '',
      pageSize: queryParams.pageSize || 25,
      pageNumber: pageParam,
    }),
    getNextPageParam: (lastPage) => (lastPage.pageNumber < lastPage.total ? lastPage.pageNumber + 1 : undefined),
    ...options,
  });
};

/**
 * Get airport by ID
 * @param {string} airportId - The airport ID.
 * @param {object} [params] The params
 * @param {{search?: string, pageSize?: number}} [params.queryParams] The search params.
 * @param {object} [params.options] The query options.
 * @returns {import('@tanstack/react-query').UseQueryResult<any, Error>} The query result.
 */
export const useGetAirport = (airportId, params = {}) => {
  const { options = {} } = params;
  return useQuery({
    queryKey: ['get-airport', airportId],
    queryFn: () => getAirport(airportId),
    ...options,
  });
};
